import { ChangeDetectionStrategy, Component, inject, isDevMode, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Account } from '../../../class/Account';
import { ConfigPreference, DefaultPreferences } from '../../../class/Preference';
import { User } from '../../../database/models';
import { AccountStateService } from '../../../services/account-state.service';
import { DialogService } from '../../../services/dialog.service';
import { LoadingService } from '../../../services/loading.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { PosthogService } from '../../../services/posthog.service';
import { PreferenceService } from '../../../services/preference.service';
import { TripstatsStoreService } from '../../../services/tripstats-store.service';
import { UserAdminService } from '../../../services/useradmin.service';
import { UserStateService } from '../../../services/user-state.service';
import UsersService from '../../../services/users.service';
import { noOnlyWhitespaceValidator, PATTERN_TEXT } from '../../../utils/normalize-string';
import { PARAMS } from '../../administration-tab/admin-routing.constants';
import { ContainerComponent } from '../../layout/container/container.component';
import { LayoutComponent } from '../../layout/layout.component';
import { LeftPanelComponent } from '../../layout/left-panel/left-panel.component';
import { SubNavigationItemComponent } from '../../layout/sub-navigation/sub-navigation-item/sub-navigation-item.component';
import { SubNavigationComponent } from '../../layout/sub-navigation/sub-navigation.component';
import { TitleComponent } from '../../layout/title-section/title/title.component';
import { TopBarComponent } from '../../layout/top-bar/top-bar.component';
import { PhoneInputComponent } from '../../shared/library/phone-input/phone-input.component';

declare var window: any;
@Component({
  selector: 'my-account',
  imports: [
    TopBarComponent,
    SubNavigationComponent,
    LayoutComponent,
    SubNavigationItemComponent,
    LeftPanelComponent,
    ContainerComponent,
    TranslatePipe,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    PhoneInputComponent,
    TitleComponent,
    MatIconModule,
  ],
  templateUrl: './my-account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './my-account.component.scss',
})
export class MyAccountComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private _accountStateService = inject(AccountStateService);
  private preferenceService = inject(PreferenceService);
  private tripstatsStore = inject(TripstatsStoreService);
  private translate = inject(TranslateService);
  private loadingService = inject(LoadingService);
  private usersService = inject(UsersService);
  private userAdminService = inject(UserAdminService);
  private _userStateService = inject(UserStateService);
  private _router = inject(Router);
  private _dialogService = inject(DialogService);
  public myAccount: User;
  private localeStr = this.preferenceService.$currentLang();
  private prefConfig: ConfigPreference = JSON.parse(JSON.stringify(DefaultPreferences.config));
  private _posthogService = inject(PosthogService);

  private allLoadedSubscriber: Subscription;
  public userForm: FormGroup;
  public userParamsForm: FormGroup;

  ngOnInit() {
    if (this._accountStateService.$account()) {
      this.initComponent();
    } else {
      this.allLoadedSubscriber = this.loadingService.usersLoadedEvent$.subscribe(() => {
        this.initComponent();
        this.allLoadedSubscriber.unsubscribe();
      });
    }
  }

  private initComponent() {
    this.myAccount = this._userStateService.$users()?.[this._accountStateService.$account()?.uid];
    const speedUnit = this.preferenceService.$preference()?.config?.speedUnit || this.prefConfig.speedUnit;
    const deviceView = this.preferenceService.$preference()?.config?.deviceView || this.prefConfig.deviceView;
    const language = this.preferenceService.$preference()?.config?.language || this.prefConfig.language;

    this.userForm = this.formBuilder.group({
      firstname: [
        this.myAccount?.firstname || this._accountStateService.$account()?.firstname,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          Validators.pattern(PATTERN_TEXT),
          noOnlyWhitespaceValidator,
        ],
      ],
      lastname: [
        this.myAccount?.lastname || this._accountStateService.$account()?.lastname,
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
          Validators.pattern(PATTERN_TEXT),
          noOnlyWhitespaceValidator,
        ],
      ],
      mail: [
        this.myAccount?.mail || this._accountStateService.$account()?.user_mail,
        [Validators.required, Validators.email, Validators.minLength(2), Validators.maxLength(255)],
      ],
      phone: [this.myAccount?.phoneE164 || this._accountStateService.$account()?.phoneE164],
    });

    this.userParamsForm = this.formBuilder.group({
      language: [language],
      speedUnit: [speedUnit],
      deviceView: [deviceView],
    });

    if ((!this.myAccount?.is_administrator && !this.myAccount?.is_supervisor) || !this.myAccount) {
      this.userForm.disable();
    }
  }

  // TODO: revoir la navigation de la méthode sendUser de userAdminService

  // TODO: simplify this method when language is in preferences

  public updateMyAccount() {
    if (this.userForm.dirty) {
      this.myAccount = this.userAdminService.cloneUser(this.myAccount);
      Object.assign(this.myAccount, this.userForm.value);
      this.usersService.updateUser(this.myAccount);
    }

    if (this.userParamsForm.dirty) {
      if (this.userParamsForm.get('speedUnit').dirty) this.resetTripStats();

      if (this.userParamsForm.get('language').dirty) this.goToPage(this.userParamsForm.get('language').value);
      this.preferenceService.setPreferences('config', {
        speedUnit: this.userParamsForm.get('speedUnit').value,
        deviceView: this.userParamsForm.get('deviceView').value,
        language: this.userParamsForm.get('language').value,
      });

      history.back();
    }
  }

  public goToPage(lang: string) {
    this.translate.use(lang);
    this.preferenceService.$currentLang.set(lang);
    if (!isDevMode()) {
      const href = {
        fr: '../fr-FR/#' + PARAMS,
        en: '../en-US/#' + PARAMS,
      };
      if (this.localeStr !== lang) window.location.href = href[lang];
    }
  }

  // TODO: check if necessary to reset trip stats
  private resetTripStats() {
    LocalStorageService.removeElement('tripstats');
    this.tripstatsStore.$tripstats.set({});
  }

  public logout() {
    this._posthogService.captureEvent('logout', { source: 'component', from: 'my-account' });
    LocalStorageService.removeElement('accountConnect');
    this.loadingService.logout();
    window.google?.accounts.id.disableAutoSelect();
  }

  public goToChangePassword() {
    this._accountStateService.updateAccount(account =>
      account
        ? new Account({
            ...account,
            edit_password: true,
          })
        : account
    );
    this._router.navigate(['/change-password']);
  }

  resetApp() {
    this._dialogService.open({
      title: 'USER.RESET_SESSION',
      content: 'USER.RESET_SESSION_CONTENT',
      onConfirm: () => {
        this.loadingService.resetApp();
      },
    });
  }
}
